import { useParams, useNavigate } from "react-router-dom";
import { articleContent, companyInfo } from "@/mocks/help-center";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const article = slug ? articleContent[slug] : undefined;

  if (!article) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cream via-white to-ice flex flex-col items-center justify-center px-4">
        <h1 className="text-2xl font-bold text-primary mb-4 font-heading">Article not found</h1>
        <button
          onClick={() => navigate("/")}
          className="text-primary underline hover:no-underline cursor-pointer"
        >
          Back to Help Center
        </button>
      </div>
    );
  }

  const lines = article.content.split("\n").filter((line) => line.trim() !== "");

  const elements: JSX.Element[] = [];
  let currentList: { type: "ol" | "ul"; items: string[] } | null = null;

  const flushList = () => {
    if (!currentList || currentList.items.length === 0) return;
    const listClass = "list-inside space-y-3 text-gray-600 mb-6";
    if (currentList.type === "ol") {
      elements.push(
        <ol key={`list-${elements.length}`} className={`${listClass} list-decimal`}>
          {currentList.items.map((item, i) => (
            <li key={i} className="text-base md:text-lg leading-relaxed pl-1">
              {item.replace(/^\d+\.\s*/, "")}
            </li>
          ))}
        </ol>
      );
    } else {
      elements.push(
        <ul key={`list-${elements.length}`} className={`${listClass} list-disc`}>
          {currentList.items.map((item, i) => (
            <li key={i} className="text-base md:text-lg leading-relaxed pl-1">
              {item.replace("- ", "")}
            </li>
          ))}
        </ul>
      );
    }
    currentList = null;
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();
    const isNumbered = /^\d+\.\s/.test(trimmed);
    const isBullet = /^- /.test(trimmed);

    if (isNumbered) {
      if (!currentList) {
        currentList = { type: "ol", items: [trimmed] };
      } else if (currentList.type === "ol") {
        currentList.items.push(trimmed);
      } else {
        flushList();
        currentList = { type: "ol", items: [trimmed] };
      }
    } else if (isBullet) {
      if (!currentList) {
        currentList = { type: "ul", items: [trimmed] };
      } else if (currentList.type === "ul") {
        currentList.items.push(trimmed);
      } else {
        flushList();
        currentList = { type: "ul", items: [trimmed] };
      }
    } else {
      flushList();
      elements.push(
        <p key={`p-${elements.length}`} className="text-base md:text-lg text-gray-600 leading-relaxed mb-6">
          {trimmed}
        </p>
      );
    }
  });

  flushList();

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream via-white to-ice">
      <header className="bg-white/70 backdrop-blur-md border-b border-cream-dark/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-4 sm:py-5">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-primary hover:opacity-70 transition-opacity cursor-pointer"
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-ice">
              <i className="ri-arrow-left-line text-xl text-primary" />
            </div>
            <span className="font-semibold text-sm md:text-base">
              Back to Help Center
            </span>
          </button>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 sm:px-8 py-12 md:py-16">
        <div className="bg-white rounded-2xl shadow-[0_2px_12px_rgba(15,64,143,0.06)] border border-cream-dark/40 p-8 md:p-12">
          <h1 className="text-2xl md:text-4xl font-bold text-primary mb-6 tracking-tight font-heading">
            {article.title}
          </h1>
          <div className="w-12 h-1 bg-accent mb-8 rounded-full" />
          <div>
            {elements}
          </div>
        </div>
      </main>
      <footer className="bg-primary text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 text-center">
          <p className="text-sm text-white/60">
            {companyInfo.name} — Help Center
          </p>
        </div>
      </footer>
    </div>
  );
}